import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { toast } from "sonner";
import { IMAGES } from "../../lib/assets";
import { MaskLines } from "./Reveal";

export const Hero = () => {
  const lenis = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] w-full overflow-hidden" data-testid="hero">
      {/* Parallax skyline */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.heroSkyline}
          alt="Houston skyline at dusk"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-ink/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/30 via-transparent to-ink/30" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col items-center justify-center text-center"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="overline mb-8"
          data-testid="hero-overline"
        >
          Houston, Texas · Title & Escrow
        </motion.div>

        <MaskLines
          lines={["Houston's New Standard", "in Title & Escrow"]}
          delay={0.5}
          testid="hero-heading"
          className="font-serif font-light text-ivory leading-[1.02] text-[clamp(2.25rem,6vw,5.5rem)] tracking-tight text-center mx-auto max-w-[92vw]"
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-10 max-w-xl mx-auto text-taupe text-base md:text-lg font-light leading-relaxed"
          data-testid="hero-subheadline"
        >
          A luxury title experience built around precision, communication and confidence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <button
            onClick={() =>
              toast("Launching Soon", {
                description: "We're completing licensing. Join the list to be the first to know.",
              })
            }
            data-testid="hero-coming-soon-btn"
            className="group relative overflow-hidden bg-ivory text-ink text-[0.72rem] uppercase tracking-[0.2em] px-9 py-4"
          >
            <span className="relative z-10">Coming Soon</span>
            <span className="absolute inset-0 bg-gold translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
          </button>
          <button
            onClick={() => lenis?.scrollTo("#connect", { offset: -20, duration: 1.6 })}
            data-testid="hero-be-notified-btn"
            className="group text-ivory text-[0.72rem] uppercase tracking-[0.2em] px-2 py-4 border-b border-gold/50 hover:border-gold transition-colors duration-300"
          >
            Be Notified
            <span className="ml-3 inline-block transition-transform duration-300 group-hover:translate-x-1 text-gold">→</span>
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
      >
        <span className="overline text-[0.55rem] text-taupe">Scroll</span>
        <motion.span
          animate={{ height: [8, 26, 8], opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="w-px bg-gold"
        />
      </motion.div>
    </section>
  );
};
